/**
 * User-scoped browser-local device preference storage.
 *
 * Device IDs and labels are browser- and device-specific. They are kept in
 * localStorage keyed by the authenticated user's stable identity so that
 * multiple users sharing a browser do not inherit each other's hardware.
 */
import { listDevices } from './media.js';

const PREFIX = 'telemed.devices.';

export function getDevicePreferenceKey(userId) {
  if (!userId) return null;
  return `${PREFIX}${userId}`;
}

export function loadDevicePreference(userId) {
  const key = getDevicePreferenceKey(userId);
  if (!key) return {};
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch (_e) {
    return {};
  }
}

export function saveDevicePreference(userId, preference) {
  const key = getDevicePreferenceKey(userId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(preference));
  } catch (_e) {
    // Storage may be unavailable in private mode; ignore silently.
  }
}

export function clearDevicePreference(userId) {
  const key = getDevicePreferenceKey(userId);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch (_e) {}
}

function findAvailableDevice(savedId, available) {
  if (savedId) {
    const exact = available.find((d) => d.deviceId === savedId);
    if (exact) {
      return { deviceId: exact.deviceId, label: exact.label, saved: true, available: true };
    }
  }
  const fallback = available[0] || null;
  if (fallback) {
    return {
      deviceId: fallback.deviceId,
      label: fallback.label,
      saved: false,
      available: true,
      savedMissing: Boolean(savedId),
    };
  }
  return { deviceId: null, label: null, saved: false, available: false, savedMissing: Boolean(savedId) };
}

/**
 * Return the currently available camera and microphone, selecting the user's
 * saved preference when it still exists and falling back to the first available
 * device otherwise.
 */
export async function resolvePreferredDevices(userId) {
  const preference = loadDevicePreference(userId);
  const devices = await listDevices();
  const camera = findAvailableDevice(preference.cameraId, devices.cameras);
  const microphone = findAvailableDevice(preference.micId, devices.microphones);
  const speaker = findAvailableDevice(preference.speakerId, devices.speakers);
  return { devices, camera, microphone, speaker };
}

/**
 * Update a single saved device preference entry.
 */
export function savePreferredDevice(userId, kind, deviceId) {
  const preference = loadDevicePreference(userId);
  const key = kind === 'camera' ? 'cameraId' : kind === 'microphone' ? 'micId' : kind === 'speaker' ? 'speakerId' : null;
  if (!key) return;
  preference[key] = deviceId;
  saveDevicePreference(userId, preference);
}
